import { QueryKey, UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import axios from 'axios'
import { MaybeRef, Ref, computed, unref } from 'vue'

import { authAxios } from '../utils/axios.util'
import { generateQueryKeys } from '../utils/query-key'
import type {
  ApiError,
  GetStartedSendInviteProps,
  UserFavourites,
  UserPersonalData,
  UserPicture,
  UserQueryParams,
  UserResponse,
  UserToken,
  UsersResponse
} from '@metras/frontend-shared/interfaces'

export const userKeys = generateQueryKeys('users')

export function useUserTokenQuery(options?: Partial<UseQueryOptions<UserToken>>) {
  return useQuery({
    queryKey: userKeys.detail('token'),
    queryFn: () => {
      return authAxios.get<UserToken>('/api-workspace/v1/users/token').then((result) => result.data)
    },
    ...options
  })
}

function computeQueryParams(params: UserQueryParams) {
  return computed(() => {
    const query = new URLSearchParams()

    if (unref(params.limit)) {
      query.append('limit', String(unref(params.limit)))
    }

    if (unref(params.page)) {
      query.append('page', String(unref(params.page)))
    }

    if (unref(params.roles) && unref(params.roles).length) {
      unref(params.roles).forEach((role) => query.append('roles', role))
    }

    if (unref(params.permissions) && unref(params.permissions).length) {
      unref(params.permissions).forEach((permission) => query.append('permissions', permission))
    }

    if (unref(params.emailVerified)) {
      query.append('emailVerified', String(unref(params.emailVerified)))
    }

    return query
  })
}

export function useUsersQuery<T = UsersResponse>(options?: Partial<UseQueryOptions<UsersResponse, unknown, T>>, queryParams: UserQueryParams = {}) {
  const params = computeQueryParams(queryParams)

  return useQuery({
    queryKey: computed(() => userKeys.list([params.value.toString()])),
    queryFn: () => {
      return axios.get(`/api-workspace/v1/users?${params.value}`).then((result) => result.data)
    },
    ...options
  })
}

export function useAllUsersQuery<T = UserResponse[]>(options?: Partial<UseQueryOptions<UserResponse[], unknown, T>>, queryParams: UserQueryParams = {}) {
  const params = computeQueryParams(queryParams)

  return useQuery({
    queryKey: computed(() => userKeys.list([params.value.toString()])),
    queryFn: () => {
      return axios.get(`/api-workspace/v1/users/all?${params.value}`).then((result) => result.data)
    },
    ...options
  })
}

export function useUsersComboQuery(options?: Partial<UseQueryOptions<UserResponse[], unknown, { id: string, name: string }[]>>, params?: UserQueryParams) {
  return useAllUsersQuery<{ id: string, name: string }[]>(
    {
      ...options,
      select: (data) =>
        data.map((user) => ({
          id: user.id,
          name: user.name
        }))
    },
    params
  )
}

export function useModifyUser(options?: Partial<UseMutationOptions<UserPersonalData, unknown, Partial<UserPersonalData>, QueryKey>>) {
  const queryClient = useQueryClient()
  const invalidateKey: QueryKey | undefined = userKeys.all

  return useMutation<UserPersonalData, unknown, Partial<UserPersonalData>, QueryKey>(
    async (updates) => {
      const response = await axios.patchForm<UserPersonalData>('api-workspace/v1/users/token', updates)

      return response.data
    },
    {
      onSuccess: async () => {
        if (invalidateKey) {
          await queryClient.invalidateQueries(invalidateKey)
        }
      },
      ...options
    }
  )
}

export function usePatchUserRoles(
  invalidateKey: QueryKey | undefined = userKeys.all,
  options?: Partial<
    UseMutationOptions<MaybeRef<string>, ApiError, { userId: MaybeRef<string>, userRoles: { roles?: string[], companyRoles?: string[], confidentalLevel?: number } }, unknown>
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn({ userId, userRoles }) {
      return axios.patch(`/api-workspace/v1/users/${unref(userId)}`, userRoles)
    },
    onSuccess() {
      if (invalidateKey) {
        return queryClient.invalidateQueries(invalidateKey)
      }
    },
    ...options
  })
}

export function useGetUserProfileData<T = UserResponse>(options?: Partial<UseQueryOptions<UserResponse, unknown, T>>) {
  return useQuery({
    queryKey: userKeys.details(),
    queryFn: () => {
      return axios.get('/api-workspace/v1/users/profile').then((result) => result.data)
    },
    ...options
  })
}

export function useGetUserPicture<T = UserPicture>(userId: Ref<string>, options?: Partial<UseQueryOptions<UserPicture, unknown, T>>) {
  return useQuery({
    queryKey: computed(() => userKeys.detail(userId + 'picture')),
    queryFn: () => {
      return axios.get(`/api-workspace/v1/users/${unref(userId)}/picture`).then((result) => result.data)
    },
    ...options
  })
}

export function useGetUserFavourites(limit: MaybeRef<number> = 20, page: MaybeRef<number> = 1, options?: Partial<UseQueryOptions<UserFavourites>>) {
  return useQuery({
    queryKey: computed(() => userKeys.list(['favourites', limit, page])),
    queryFn: () => {
      return axios.get(`/api-workspace/v1/users/token/favourites?page=${unref(page)}&limit=${unref(limit)}`).then((result) => result.data)
    },
    ...options
  })
}

export function useDeleteUserMutation(invalidateKey: QueryKey | undefined = userKeys.all, options?: Partial<UseMutationOptions<unknown, ApiError, MaybeRef<string>, unknown>>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn(userId: MaybeRef<string>) {
      return axios.delete(`/api-workspace/v1/users/${userId}`)
    },
    onSuccess() {
      if (invalidateKey) {
        return queryClient.invalidateQueries(invalidateKey)
      }
    },
    ...options
  })
}

export function usePostTeamInvite(invalidateKey: QueryKey = userKeys.all, options?: Partial<UseMutationOptions<unknown, ApiError, Partial<GetStartedSendInviteProps>, unknown>>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn(users) {
      return axios.post('/api-workspace/v1/users/invite', users)
    },
    onSuccess() {
      if (invalidateKey) {
        return queryClient.invalidateQueries(invalidateKey)
      }
    },
    ...options
  })
}
